/**
 * SpeechRecorder — Record and transcribe live public speeches.
 *
 * Browser MediaRecorder → 5s chunks → backend → ElevenLabs Scribe → live transcript.
 * Save to public_speeches table. Distribute to AI agents.
 *
 * SPEC: MODULES/M8_PUBLIC_SCREEN/SPEC_SPEECH_RECORDING.md
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

interface Speech {
  id: string
  round_num: number
  speaker_role_id: string
  speaker_name: string
  speaker_country: string | null
  context: string
  transcript: string
  duration_seconds: number
  distributed: boolean
  created_at: string
}

interface ParticipantOption {
  id: string
  name: string
  country: string
}

const API_BASE = import.meta.env.VITE_API_URL ?? ''
const CHUNK_INTERVAL_MS = 5000

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

export function SpeechRecorder({ simId }: { simId: string }) {
  // Recording state
  const [recording, setRecording] = useState(false)
  const [paused, setPaused] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [elapsed, setElapsed] = useState(0)
  const [processing, setProcessing] = useState(false)

  // Config
  const [speaker, setSpeaker] = useState<string>('')
  const [speakerName, setSpeakerName] = useState('')
  const [speakerCountry, setSpeakerCountry] = useState('')
  const [context, setContext] = useState('')
  const [autoDistribute, setAutoDistribute] = useState(true)
  const [participants, setParticipants] = useState<ParticipantOption[]>([])

  // History
  const [speeches, setSpeeches] = useState<Speech[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [distributing, setDistributing] = useState<string | null>(null)

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const chunkTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number>(0)
  const transcriptRef = useRef('')
  const recordingRef = useRef(false)
  const pausedRef = useRef(false)

  // Load participants (human + moderator option)
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('roles')
        .select('id,character_name,country_code,is_ai_operated')
        .eq('sim_run_id', simId)
        .eq('status', 'active')
      const humans = (data || [])
        .filter(r => !r.is_ai_operated)
        .map(r => ({ id: r.id, name: r.character_name, country: r.country_code }))
      setParticipants([{ id: 'moderator', name: 'Moderator', country: '' }, ...humans])
    }
    load()
  }, [simId])

  // Load speech history
  const loadSpeeches = useCallback(async () => {
    const token = await getToken()
    const resp = await fetch(`${API_BASE}/api/sim/${simId}/speeches`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (resp.ok) {
      const json = await resp.json()
      setSpeeches(json.data?.speeches || [])
    }
  }, [simId])

  useEffect(() => {
    loadSpeeches()
    const ch = supabase.channel(`speeches-${simId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'public_speeches', filter: `sim_run_id=eq.${simId}` }, () => loadSpeeches())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId, loadSpeeches])

  // Speaker selection handler
  const handleSpeakerChange = (roleId: string) => {
    setSpeaker(roleId)
    const p = participants.find(p => p.id === roleId)
    if (p) {
      setSpeakerName(p.name)
      setSpeakerCountry(p.country)
    }
  }

  // Record one chunk: start recorder, wait CHUNK_INTERVAL, stop, send blob
  const recordAndSendChunk = useCallback(async () => {
    const stream = streamRef.current
    if (!stream || !recordingRef.current || pausedRef.current) return

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus' : 'audio/webm'

    return new Promise<void>((resolve) => {
      const recorder = new MediaRecorder(stream, { mimeType })
      const chunks: Blob[] = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }

      recorder.onstop = async () => {
        if (chunks.length === 0) { resolve(); return }
        const blob = new Blob(chunks, { type: mimeType })
        if (blob.size < 100) { resolve(); return }

        setProcessing(true)
        try {
          const token = await getToken()
          const resp = await fetch(`${API_BASE}/api/sim/${simId}/transcribe`, {
            method: 'POST',
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: blob,
          })
          if (resp.ok) {
            const json = await resp.json()
            const text = json.data?.text || ''
            if (text.trim()) {
              transcriptRef.current += (transcriptRef.current ? ' ' : '') + text.trim()
              setTranscript(transcriptRef.current)
            }
          }
        } catch { /* chunk lost */ }
        finally { setProcessing(false) }
        resolve()
      }

      recorder.start()
      setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop()
      }, CHUNK_INTERVAL_MS)
    })
  }, [simId])

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      setTranscript('')
      transcriptRef.current = ''
      setElapsed(0)
      startTimeRef.current = Date.now()
      setRecording(true)
      recordingRef.current = true
      setPaused(false)
      pausedRef.current = false

      // Elapsed timer
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000))
      }, 1000)

      // Chunk loop: record 5s, send, repeat
      const chunkLoop = async () => {
        while (recordingRef.current) {
          if (!pausedRef.current) {
            await recordAndSendChunk()
          } else {
            await new Promise(r => setTimeout(r, 500))
          }
        }
      }
      chunkLoop()
    } catch {
      alert('Microphone access denied or unavailable')
    }
  }

  // Pause/resume
  const togglePause = () => {
    if (paused) {
      setPaused(false)
      pausedRef.current = false
    } else {
      setPaused(true)
      pausedRef.current = true
    }
  }

  // Stop and save
  const stopRecording = async () => {
    recordingRef.current = false
    setRecording(false)
    setPaused(false)
    pausedRef.current = false
    streamRef.current?.getTracks().forEach(t => t.stop())
    if (timerRef.current) clearInterval(timerRef.current)

    // Wait for last chunk to finish processing
    await new Promise(r => setTimeout(r, 1500))

    const finalTranscript = transcriptRef.current.trim()
    if (!finalTranscript) return

    // Save to DB
    try {
      const token = await getToken()
      await fetch(`${API_BASE}/api/sim/${simId}/speeches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          speaker_role_id: speaker || 'moderator',
          speaker_name: speakerName || 'Moderator',
          speaker_country: speakerCountry || null,
          context,
          transcript: finalTranscript,
          duration_seconds: elapsed,
          auto_distribute: autoDistribute,
        }),
      })
      setTranscript('')
      transcriptRef.current = ''
      loadSpeeches()
    } catch (err) {
      alert('Failed to save speech')
    }
  }

  // Distribute
  const handleDistribute = async (speechId: string) => {
    setDistributing(speechId)
    try {
      const token = await getToken()
      await fetch(`${API_BASE}/api/sim/${simId}/speeches/${speechId}/distribute`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      loadSpeeches()
    } catch { alert('Distribution failed') }
    finally { setDistributing(null) }
  }

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  return (
    <div className="space-y-3">
      <h3 className="font-heading text-body-sm font-semibold text-text-primary">Speech Recording</h3>

      {/* Config row */}
      {!recording && (
        <div className="flex items-center gap-2 flex-wrap">
          <select value={speaker} onChange={e => handleSpeakerChange(e.target.value)}
            className="bg-card border border-border rounded px-2 py-1.5 font-body text-caption text-text-primary focus:outline-none focus:border-action min-w-[140px]">
            <option value="">Select speaker...</option>
            {participants.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.country ? ` (${p.country})` : ''}
              </option>
            ))}
          </select>
          <input value={context} onChange={e => setContext(e.target.value)}
            placeholder="Context (optional)"
            className="flex-1 bg-card border border-border rounded px-2 py-1.5 font-body text-caption text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:border-action min-w-[150px]" />
          <label className="flex items-center gap-1.5 font-body text-caption text-text-secondary cursor-pointer">
            <input type="checkbox" checked={autoDistribute} onChange={e => setAutoDistribute(e.target.checked)}
              className="rounded border-border" />
            Auto-distribute to AI
          </label>
        </div>
      )}

      {/* Record controls */}
      <div className="flex items-center gap-2">
        {!recording ? (
          <button onClick={startRecording} disabled={!speaker}
            className="font-body text-caption font-medium bg-danger/15 text-danger px-3 py-1.5 rounded hover:bg-danger/25 disabled:opacity-30 transition-colors flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-danger" />
            Start Recording
          </button>
        ) : (
          <>
            <button onClick={togglePause}
              className="font-body text-caption font-medium bg-warning/10 text-warning px-3 py-1.5 rounded hover:bg-warning/20 transition-colors">
              {paused ? '▶ Resume' : '⏸ Pause'}
            </button>
            <button onClick={stopRecording}
              className="font-body text-caption font-medium bg-text-secondary/10 text-text-primary px-3 py-1.5 rounded hover:bg-text-secondary/20 transition-colors">
              ⏹ Stop & Save
            </button>
            <span className="font-data text-caption text-text-secondary">
              {formatTime(elapsed)}
            </span>
            {processing && (
              <span className="font-data text-[10px] text-action animate-pulse">transcribing...</span>
            )}
            {paused && (
              <span className="font-data text-[10px] text-warning">PAUSED</span>
            )}
          </>
        )}
      </div>

      {/* Live transcript */}
      {(recording || transcript) && (
        <div className="bg-base border border-border rounded p-3 max-h-[200px] overflow-y-auto">
          <div className="font-body text-[10px] text-text-secondary/50 uppercase tracking-wider mb-1">
            {recording ? 'Live Transcript' : 'Last Transcript'}
          </div>
          <div className="font-body text-body-sm text-text-primary leading-relaxed whitespace-pre-wrap">
            {transcript || <span className="text-text-secondary/30 italic">Waiting for speech...</span>}
          </div>
        </div>
      )}

      {/* Speech History */}
      {speeches.length > 0 && (
        <div className="space-y-1">
          <div className="font-body text-[10px] text-text-secondary/50 uppercase tracking-wider">Speech History</div>
          {speeches.map(s => (
            <div key={s.id} className="bg-base border border-border rounded">
              <div className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-card/50 transition-colors"
                onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}>
                <span className="font-body text-[10px] text-text-secondary/50">{expandedId === s.id ? '▾' : '▸'}</span>
                <span className="font-data text-caption text-text-secondary">R{s.round_num}</span>
                <span className="font-body text-caption text-text-primary font-medium">{s.speaker_name}</span>
                {s.context && <span className="font-body text-caption text-text-secondary truncate">— {s.context}</span>}
                <span className="font-data text-[10px] text-text-secondary/50 ml-auto">{formatTime(s.duration_seconds)}</span>
                {s.distributed && <span className="font-data text-[10px] text-success">Distributed ✓</span>}
              </div>
              {expandedId === s.id && (
                <div className="px-3 pb-2 border-t border-border/50">
                  <div className="font-body text-body-sm text-text-primary leading-relaxed py-2 whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                    {s.transcript}
                  </div>
                  <div className="flex gap-2 pt-1">
                    {!s.distributed && (
                      <button onClick={() => handleDistribute(s.id)}
                        disabled={distributing === s.id}
                        className="font-body text-[10px] font-medium bg-action/10 text-action px-2 py-0.5 rounded hover:bg-action/20 disabled:opacity-40 transition-colors">
                        {distributing === s.id ? 'Sending...' : 'Distribute to AI'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
